/**
 * DriverShell — full-bleed wrapper for the driver experience.
 *
 * V2 architecture: the marketplace map IS the driver app. Every other
 * surface (account, vehicles, documents, my blocks, earnings) lives
 * inside a Rider-style bottom drawer that overlays the map. No top
 * status bar, no bottom tab bar, no nested routing — the chrome lives
 * inside DriverMap itself.
 *
 * Exports `useResolvedTheme`, `STATUS_LABEL`, and `STATUS_DOT` for any
 * descendants that still need them (Account drawer panels).
 */
import React, { useEffect, useState } from "react";

export const STATUS_LABEL = {
  offline: "Offline",
  available: "Online",
  busy: "Unavailable",
  unavailable: "Unavailable",
  on_block: "On Block",
  navigating: "Navigating",
};

export const STATUS_DOT = {
  offline: "bg-[#515E7B]/35",
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  unavailable: "bg-amber-500",
  on_block: "bg-[#ed6500]",
  navigating: "bg-[#ed6500]",
};

export const STATUS_TEXT = STATUS_LABEL;

export function useResolvedTheme(theme) {
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches
  );
  useEffect(() => {
    if (theme !== "system") return undefined;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [theme]);
  if (theme === "light") return "light";
  if (theme === "dark") return "dark";
  return systemDark ? "dark" : "light";
}

export default function DriverShell({ children }) {
  // Lock the doc theme attribute to light — the driver UI is now
  // unambiguously light to match the Rider App. Settings inside the
  // drawer can still toggle this for personal preference.
  useEffect(() => {
    document.documentElement.dataset.driverTheme = "light";
  }, []);
  return (
    <div className="fixed inset-0 bg-[#F9F7F4] text-[#0A1128]" data-testid="driver-shell" data-theme="light">
      {children}
    </div>
  );
}
